'use client';

import { ExclusiveOfferTypeEnum } from '@hyphengroup/cms-components/types/cmsEnums';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useMemo, useState } from 'react';
import { filterActiveOffers } from '../../lib/helpers';
import { trackApplyV3RewardList } from '../../lib/tracking';
import { RewardCardTranslations } from './i18n';
import { RewardCard } from './RewardCard';
import { RewardList } from './RewardList';
import {
  CmsApplyRewardProduct,
  CmsOffer,
} from '$/(cms)/_modules/cmsService/types';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type RewardCatalogueProps = CmsApplyRewardProduct & {
  displayFlash: boolean;
};

export const RewardCatalogue: React.FC<RewardCatalogueProps> = ({
  displayFlash,
  catalogueProduct,
  productDisplayName,
  productId,
}) => {
  const context = useClientContext();
  const staticText = getClientTranslations(RewardCardTranslations, context);
  const [isListOpen, setIsListOpen] = useState(false);
  const listId = `reward-list-${productId}-${displayFlash ? 'flash' : 'regular'}`;

  const activeExclusiveOffers = useMemo(
    () => filterActiveOffers(catalogueProduct?.exclusive_offers),
    [catalogueProduct?.exclusive_offers],
  );
  const activeWelcomeOffers = useMemo(
    () => filterActiveOffers(catalogueProduct?.welcome_offers),
    [catalogueProduct?.welcome_offers],
  );

  const heroOffer = useMemo(() => {
    if (!catalogueProduct) {
      return undefined;
    }
    const eligibleExclusive = activeExclusiveOffers.find((offer) =>
      displayFlash
        ? offer.exclusive_offer_type === ExclusiveOfferTypeEnum.Flash
        : offer.exclusive_offer_type !== ExclusiveOfferTypeEnum.Flash,
    );
    return eligibleExclusive ?? activeWelcomeOffers[0];
  }, [
    catalogueProduct,
    displayFlash,
    activeExclusiveOffers,
    activeWelcomeOffers,
  ]);

  const heroGift = heroOffer?.list_of_gifts?.[0]?.gift;

  const hasRewardList = useMemo(() => {
    const offerHasGift = (offer: CmsOffer) =>
      offer.list_of_gifts?.some((giftList) => giftList.gift) ?? false;

    if (displayFlash) {
      return activeExclusiveOffers.some(
        (offer) =>
          offer.exclusive_offer_type === ExclusiveOfferTypeEnum.Flash &&
          offerHasGift(offer),
      );
    }

    return (
      activeExclusiveOffers.some(
        (offer) =>
          offer.exclusive_offer_type !== ExclusiveOfferTypeEnum.Flash &&
          offerHasGift(offer),
      ) || activeWelcomeOffers.some(offerHasGift)
    );
  }, [displayFlash, activeExclusiveOffers, activeWelcomeOffers]);

  if (!catalogueProduct || (!heroGift && !hasRewardList)) {
    return null;
  }

  return (
    <div>
      {heroGift && (
        <RewardCard
          desktop_image={heroGift.desktop_image}
          mobile_image={heroGift.mobile_image}
          gift_title={heroGift.gift_title}
          additional_information={heroGift.additional_information}
          badge_tag={heroOffer?.badge_tag}
          badge_color={heroOffer?.badge_color}
        />
      )}
      {hasRewardList && (
        <>
          <button
            type="button"
            onClick={() =>
              setIsListOpen((prev) => {
                if (!prev) {
                  trackApplyV3RewardList({
                    catalogueProduct,
                    productDisplayName,
                    productId,
                  });
                }
                return !prev;
              })
            }
            aria-expanded={isListOpen}
            aria-controls={listId}
            className={classNames(
              'mhg-alpha-btn-small-text flex w-full items-center gap-button/size border border-borders/default px-spacing/padding/x py-spacing/padding/y-sm bg-fill/card text-buttons/neutral',
              {
                'rounded-b-radius/card': !isListOpen,
              },
            )}
          >
            <MaterialIcon icon={isListOpen ? 'remove' : 'add'} px={24} />
            {tr(isListOpen ? 'minimiseRewardList' : 'fullCardList', staticText)}
          </button>
          {isListOpen && (
            <RewardList
              id={listId}
              displayFlash={displayFlash}
              staticText={staticText}
              {...catalogueProduct}
              exclusive_offers={activeExclusiveOffers}
              welcome_offers={activeWelcomeOffers}
            />
          )}
        </>
      )}
    </div>
  );
};
